import BraintreeClient from './BraintreeClient';
import {
	displayPaymentInfo,
	clearPaymentInfo,
	displayPaymentError,
} from './braintreePaymentInfoSection';

/**
 * A mapping of constants for DOM selectors, storage keys, and class names
 * to prevent magic strings and improve maintainability.
 */
const CONSTANTS = {
	BUTTON_ID: 'braintree-local-payment-button',
	ICON_ID: 'local-payment-button-icon',
	INFO_SECTION_ID: 'braintree-payment-info-section',
	LOADING_CLASS: 'btn--is-waiting',
	STORAGE_ACTIVE_KEY: 'braintreeLocalPaymentActive',
	STORAGE_TIMESTAMP_KEY: 'braintreeLocalPaymentTimestamp',
	STALE_THRESHOLD_MS: 5 * 60 * 1000, // 5 minutes
};

export default class BraintreeLocalPayment extends BraintreeClient {
	// Private class fields
	#localPaymentInstance = null;

	#staleCheckInterval = null;

	#paymentType;

	#fallbackButtonLabel;

	#paymentButton;

	#iconElement;

	#infoSection;

	#isPaymentInitiator = false;

	#translations = window.errorTranslations?.braintree?.localpayment || {};

	constructor() {
		super();

		// --- 1. Fail-fast element discovery and property initialization ---
		this.#paymentButton = document.getElementById(CONSTANTS.BUTTON_ID);
		this.#iconElement = document.getElementById(CONSTANTS.ICON_ID);
		this.#infoSection = document.getElementById(CONSTANTS.INFO_SECTION_ID);
		this.#paymentType = this.dataLayer.order.paymentType.toLowerCase();

		if (!this.#paymentButton || !this.form || !this.#infoSection) {
			displayPaymentError(
				this.#translations.init.error.replace('{0}', this.#paymentType),
			);
			return;
		}

		this.#fallbackButtonLabel =
			this.form.dataset.localPaymentFallbackButtonLabel;

		this.localPaymentButtonsConfigMap = {
			ideal: {
				name: 'ideal-button',
				iconClass: 'ideal',
			},
			bancontact: {
				name: 'bancontact-button',
				iconClass: 'bancontact',
			},
			eps: {
				name: 'eps-button',
				iconClass: 'eps',
			},
		};
		this.errorHandlersMap = {
			LOCAL_PAYMENT_WINDOW_CLOSED: this.redirectToBillingPage,
			LOCAL_PAYMENT_CANCELED:
				this.redirectToBillingPageWithErrorMessage.bind(this),
			DEFAULT: this.redirectToBillingPageWithErrorMessage.bind(this),
		};

		// --- 2. Initialize the component ---
		this.configurePaymentButton();
		this.addEventListeners();
		this.initializeBraintree();
		this.checkInitialState();
	}

	/**
	 * Sets the initial UI state when the page loads.
	 */
	checkInitialState() {
		if (this.#isPaymentActive()) {
			if (this.#isPaymentStale()) {
				this.#clearPaymentState();
			} else {
				this.toggleFormLock(true, this.#translations.session.blocked);
				this.#startStaleCheckInterval();
			}
		}
	}

	/**
	 * Configures the payment button's appearance and attributes.
	 */
	configurePaymentButton() {
		const buttonConfig = this.localPaymentButtonsConfigMap[this.#paymentType];
		if (!buttonConfig) {
			displayPaymentError(
				this.#translations.init.buttonConfig.replace('{0}', this.#paymentType),
			);
			return;
		}
		this.#paymentButton.name = buttonConfig.name;
		const iconClass = ` braintree-payment-buttons__submit-local-payment-button__icon ${buttonConfig.iconClass}`;
		this.#iconElement.className += iconClass;
		this.#paymentButton.removeAttribute('hidden');
	}

	/**
	 * Initializes the Braintree client and local payment instance.
	 */
	async initializeBraintree() {
		try {
			const client = await this.createClient();
			const { merchantAccountId } = this.form.dataset;
			this.#localPaymentInstance = await this.braintree.localPayment.create({
				client,
				merchantAccountId,
			});

			if (this.#localPaymentInstance.hasTokenizationParams()) {
				this.toggleFormLock(true, this.#translations.session.finalizing);
				this.termsAndConditionsCheckbox.checked = true;
				const { nonce } = await this.#localPaymentInstance.tokenize();
				this.submitFormWithBraintreeNonce(this.form, nonce);
			}
		} catch (error) {
			displayPaymentError(
				this.#translations.init.error.replace('{0}', this.#paymentType),
			);
			this.handleError(error);
		}
	}

	/**
	 * Adds all necessary event listeners.
	 */
	addEventListeners() {
		this.#paymentButton.addEventListener(
			'click',
			this.handlePaymentClick.bind(this),
		);

		window.addEventListener('storage', event => {
			if (event.key === CONSTANTS.STORAGE_ACTIVE_KEY) {
				const isActive = event.newValue === 'true';
				if (isActive) {
					this.toggleFormLock(true, this.#translations.session.blocked);
				} else {
					this.toggleFormLock(false);
				}
			}
		});

		window.addEventListener('beforeunload', () => {
			if (this.#isPaymentActive() && this.#isPaymentInitiator) {
				this.#clearPaymentState();
				this.#localPaymentInstance?.teardown();
			}
		});
	}

	/**
	 * Orchestrates the entire payment process on button click.
	 */
	async handlePaymentClick(event) {
		event.preventDefault();
		this.#isPaymentInitiator = true;

		if (!this.termsAndConditionsCheckbox.checked) {
			this.form.requestSubmit();
			return;
		}

		this.toggleFormLock(true, this.#translations.session.redirecting);
		this.#setPaymentActiveState();
		this.#startStaleCheckInterval();

		try {
			const { cartData } = this.getCartDataSync();
			const { nonce } = await this.startPayment(cartData);
			this.submitFormWithBraintreeNonce(this.form, nonce);
		} catch (error) {
			displayPaymentError(error);
			this.handleError(error);
		} finally {
			this.#isPaymentInitiator = false;
			this.#clearPaymentState();
			this.toggleFormLock(false);
		}
	}

	/**
	 * Initiates the Braintree local payment flow.
	 */
	startPayment(cartData) {
		return this.#localPaymentInstance.startPayment({
			paymentType: this.#paymentType,
			currencyCode: this.currency,
			givenName: cartData.firstName,
			surname: cartData.lastName,
			amount: cartData.amount,
			fallback: {
				url: window.location.href,
				buttonText: this.#fallbackButtonLabel,
			},
			onPaymentStart: ({ paymentId }, start) => {
				fetch('ajax/payment/local/prepare', {
					method: 'POST',
					body: JSON.stringify({ paymentId, paymentType: this.#paymentType }),
					headers: { 'Content-Type': 'application/json' },
				});
				start();
			},
		});
	}

	/**
	 * Toggles the loading state and disabled status of the entire form.
	 */
	toggleFormLock(isLocked, message = '') {
		this.toggleLoading(isLocked);

		if (isLocked) {
			displayPaymentInfo(message);
		} else {
			clearPaymentInfo();
		}
	}

	/**
	 * Toggles the loading state of the primary payment button.
	 */
	toggleLoading(isLoading) {
		this.#paymentButton.disabled = isLoading;
		this.#paymentButton.classList.toggle(CONSTANTS.LOADING_CLASS, isLoading);
	}

	/**
	 * Centralized error handler.
	 */
	handleError(error) {
		const handler =
			this.errorHandlersMap[error.code] || this.errorHandlersMap.DEFAULT;
		handler();
	}

	redirectToBillingPageWithErrorMessage() {
		localStorage.setItem('localPaymentDeclined', 'true');
		this.redirectToBillingPage();
	}

	// --- Private Class Methods ---

	#startStaleCheckInterval() {
		if (this.#staleCheckInterval) return;

		this.#staleCheckInterval = setInterval(() => {
			if (this.#isPaymentActive() && this.#isPaymentStale()) {
				this.#clearPaymentState();
				this.toggleFormLock(false);
			}
		}, 5000);
	}

	#stopStaleCheckInterval() {
		if (this.#staleCheckInterval) {
			clearInterval(this.#staleCheckInterval);
			this.#staleCheckInterval = null;
		}
	}

	#setPaymentActiveState() {
		localStorage.setItem(CONSTANTS.STORAGE_ACTIVE_KEY, 'true');
		localStorage.setItem(
			CONSTANTS.STORAGE_TIMESTAMP_KEY,
			Date.now().toString(),
		);
	}

	#clearPaymentState() {
		localStorage.removeItem(CONSTANTS.STORAGE_ACTIVE_KEY);
		localStorage.removeItem(CONSTANTS.STORAGE_TIMESTAMP_KEY);
		this.#stopStaleCheckInterval();
	}

	#isPaymentActive() {
		return localStorage.getItem(CONSTANTS.STORAGE_ACTIVE_KEY) === 'true';
	}

	#isPaymentStale() {
		const timestamp = localStorage.getItem(CONSTANTS.STORAGE_TIMESTAMP_KEY);
		if (!timestamp) return false;
		return Date.now() - parseInt(timestamp, 10) > CONSTANTS.STALE_THRESHOLD_MS;
	}
}
